import React, { useState } from 'react';
import { X, Trash2, ShoppingBag, ShieldCheck, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import { useShop } from '../context/ShopContext';

export const CartDrawer: React.FC = () => {
  const {
    isCartOpen,
    setIsCartOpen,
    cart,
    currency,
    locale,
    t,
    updateQuantity,
    removeFromCart,
    shippingConfig,
    storeStatus
  } = useShop();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isCartOpen) return null;

  // Local estimate of subtotal and discounts
  const subtotal = cart.reduce((acc, it) => {
    const p = it.product;
    const basePrice = currency === 'CHF' ? p.price_chf : p.price_eur;
    return acc + basePrice * it.quantity;
  }, 0);

  const effectiveTotal = cart.reduce((acc, it) => {
    const p = it.product;
    const basePrice = currency === 'CHF' ? p.price_chf : p.price_eur;
    const eff = currency === 'CHF' ? (p.effective_price_chf ?? basePrice) : (p.effective_price_eur ?? basePrice);
    return acc + eff * it.quantity;
  }, 0);

  const totalDiscount = Math.max(0, subtotal - effectiveTotal);

  const freeShippingActive = Boolean(shippingConfig?.free_shipping_enabled);
  const freeThreshold = currency === 'CHF'
    ? (shippingConfig?.free_shipping_threshold_chf || 60)
    : (shippingConfig?.free_shipping_threshold_eur || 60);

  const isFree = freeShippingActive && effectiveTotal >= freeThreshold;
  const baseShipping = currency === 'CHF'
    ? (shippingConfig?.shipping_chf || 4.90)
    : (shippingConfig?.shipping_eur || 5.90);
  const shippingAmount = isFree ? 0 : baseShipping;
  const finalTotal = effectiveTotal + (cart.length > 0 ? shippingAmount : 0);

  const handleCheckout = async () => {
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const payload = {
        currency,
        locale,
        items: cart.map(it => ({
          productId: it.productId,
          variantId: it.variantId,
          quantity: it.quantity
        }))
      };

      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json() as any;

      if (!res.ok || !data.checkoutUrl) {
        setErrorMessage(data.error || 'Erreur lors de la préparation du paiement.');
        setIsSubmitting(false);
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = data.checkoutUrl;
    } catch (err: any) {
      setErrorMessage('Impossible de joindre le serveur de paiement. Veuillez réessayer.');
      setIsSubmitting(false);
    }
  };

  const salesEnabled = storeStatus?.sales_enabled !== false && storeStatus?.sales_enabled !== 0;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-950/60 backdrop-blur-xs transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col">
          {/* Drawer Header */}
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-gray-900" />
              <h2 className="font-display font-bold text-lg text-gray-900">{t.cart.title}</h2>
              <span className="text-xs font-semibold px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                {cart.reduce((acc, it) => acc + it.quantity, 0)}
              </span>
            </div>
            <button
              onClick={() => setIsCartOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl"
              aria-label="Fermer le panier"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error notice if any */}
          {errorMessage && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Cart Items List */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-4">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-400">
                  <ShoppingBag className="w-8 h-8" />
                </div>
                <p className="text-sm text-gray-500">{t.cart.empty}</p>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="px-4 py-2 bg-neutral-900 text-white text-xs font-bold rounded-xl hover:bg-pink-600 transition-colors"
                >
                  {t.cart.continueShopping}
                </button>
              </div>
            ) : (
              cart.map(it => {
                const title = locale === 'de' ? it.product.name_de : it.product.name_fr;
                const variantName = locale === 'de' ? it.variant?.name_de : it.variant?.name_fr;
                const basePrice = currency === 'CHF' ? it.product.price_chf : it.product.price_eur;
                const effPrice = currency === 'CHF'
                  ? (it.product.effective_price_chf ?? basePrice)
                  : (it.product.effective_price_eur ?? basePrice);

                return (
                  <div
                    key={`${it.productId}-${it.variantId || 'base'}`}
                    className="flex gap-4 p-3 bg-stone-50/50 rounded-2xl border border-gray-100"
                  >
                    {/* Thumbnail */}
                    <img
                      src="/images/mug-straight.jpg"
                      alt={title}
                      className="w-18 h-18 object-contain rounded-xl bg-white p-1 border border-gray-100 shrink-0"
                    />

                    {/* Details */}
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <h4 className="text-xs font-bold text-gray-900 leading-snug">{title}</h4>
                        {variantName && (
                          <p className="text-[11px] text-gray-500 mt-0.5">{variantName}</p>
                        )}
                        <p className="text-xs font-mono font-bold text-gray-900 mt-1">
                          {effPrice.toFixed(2)} {currency}
                        </p>
                      </div>

                      {/* Controls */}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200/60">
                        <div className="flex items-center border border-gray-200 bg-white rounded-lg">
                          <button
                            onClick={() => updateQuantity(it.productId, it.variantId, it.quantity - 1)}
                            className="px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 rounded-l-md"
                          >
                            -
                          </button>
                          <span className="px-2 text-xs font-mono font-semibold">{it.quantity}</span>
                          <button
                            onClick={() => updateQuantity(it.productId, it.variantId, it.quantity + 1)}
                            className="px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 rounded-r-md"
                          >
                            +
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(it.productId, it.variantId)}
                          className="text-gray-400 hover:text-red-500 p-1"
                          title={t.cart.remove}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Drawer Footer / Totals */}
          {cart.length > 0 && (
            <div className="p-5 border-t border-gray-100 bg-gray-50/50 space-y-3">
              <div className="space-y-1.5 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>{t.cart.subtotal}</span>
                  <span className="font-mono font-medium">{subtotal.toFixed(2)} {currency}</span>
                </div>

                {totalDiscount > 0 && (
                  <div className="flex justify-between text-pink-600 font-medium">
                    <span>{t.cart.discount}</span>
                    <span className="font-mono">-{totalDiscount.toFixed(2)} {currency}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span>{t.cart.shipping}</span>
                  <span className="font-mono font-medium">
                    {isFree ? (
                      <span className="text-emerald-600 font-semibold">{t.cart.freeShipping}</span>
                    ) : (
                      `${shippingAmount.toFixed(2)} ${currency}`
                    )}
                  </span>
                </div>

                <div className="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-gray-200">
                  <span>{t.cart.total}</span>
                  <span className="font-mono text-base">{finalTotal.toFixed(2)} {currency}</span>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                type="button"
                onClick={handleCheckout}
                disabled={isSubmitting || !salesEnabled}
                className="w-full py-3.5 px-4 bg-neutral-900 hover:bg-pink-600 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-md disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Redirection vers le paiement sécurisé...</span>
                  </>
                ) : (
                  <>
                    <span>{t.cart.checkout}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-2 text-[10px] text-gray-400 pt-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                <span>{t.cart.secureNotice}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
